import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { toBlob, toJpeg } from 'html-to-image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from '../../../../design';
import { useRequester } from '../../../../hooks';
import { EikenMaterialData, EikenMaterialResource } from '../../../../resources';
import type { ImageGenerationStatus } from '../../../../resources/EikenMaterialResource';
import placeholder from '../../list/components/placeholder.png';

// Set up polling configuration
const MAX_ATTEMPTS = 600; // Maximum polling attempts (600 * 10 seconds = 10 minutes)
const POLL_INTERVAL = 1000 * 10; // 10 seconds between polls

type EikenMaterialGeneratedFormData = Partial<EikenMaterialData>;

type FormData = {
  title: string;
  pronunciation: string;
  imageText: string;
  imagePrompt: string;
  pronunciationQuestion: string;
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  transitionArrowLabels: string[];
};

type GeneratingImageStatus = {
  progress: number;
  status: ImageGenerationStatus;
};

const imageConfig = {
  quality: 1,
  backgroundColor: 'white',
  canvasWidth: 1000,
  canvasHeight: 1000,
};

export function EikenMaterialGeneratedForm({
  eikenMaterial,
}: {
  eikenMaterial?: EikenMaterialData;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditTitle, setIsEditTitle] = useState(false);
  const [generatingImageStatus, setGeneratingImageStatus] = useState<GeneratingImageStatus | null>(
    null
  );
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const [referenceTextTitle, setReferenceTextTitle] = useState(
    eikenMaterial?.referenceTextTitle || ''
  );
  const [referenceText, setReferenceText] = useState(eikenMaterial?.referenceText || '');
  const [imageText, setImageText] = useState(eikenMaterial?.imageText || '');
  const [imagePrompt, setImagePrompt] = useState(eikenMaterial?.imagePrompt || '');
  const [pronunciation, setPronunciation] = useState(eikenMaterial?.content.pronunciation || '');
  const [q1, setQ1] = useState(eikenMaterial?.content.q1 || '');
  const [q2, setQ2] = useState(eikenMaterial?.content.q2 || '');
  const [q3, setQ3] = useState(eikenMaterial?.content.q3 || '');
  const [q4, setQ4] = useState(eikenMaterial?.content.q4 || '');
  const [transitionArrowLabel1, setTransitionArrowLabel1] = useState(
    eikenMaterial?.transitionArrowLabels?.[0] || ''
  );
  const [transitionArrowLabel2, setTransitionArrowLabel2] = useState(
    eikenMaterial?.transitionArrowLabels?.[1] || ''
  );

  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (eikenMaterial?.generatedImage) {
      fetch(eikenMaterial.generatedImage, { mode: 'cors', cache: 'no-cache' })
        .then((response) => response.arrayBuffer())
        .then((buffer) => {
          const base64String = btoa(
            new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          setGeneratedImageUrl(`data:image/jpeg;base64,${base64String}`);
        });
    }
  }, [eikenMaterial?.generatedImage]);

  const saveMaterial = useRequester<EikenMaterialResource>({
    onSubmit: (data: any) =>
      eikenMaterial?.id
        ? EikenMaterialResource.updateOne(eikenMaterial.id, data)
        : EikenMaterialResource.createOne(data),
    onSuccess: (ob) => navigate(`/eiken-materials`),
    onError: console.log,
  });

  const doImageUpload = useCallback(async () => {
    const imagePreview = document.getElementById('image-preview');
    if (!imagePreview || !eikenMaterial) return;

    try {
      const blob = await toBlob(imagePreview, imageConfig);
      if (!blob) throw new Error('Failed to generate image blob');

      const image = new File([blob], 'image.png', { lastModified: Date.now(), type: blob.type });

      const imageUrl = await EikenMaterialResource.upload(image, 'image');

      await EikenMaterialResource.updateOne(eikenMaterial.id, {
        image: imageUrl,
        generatedImageStatus: 'completed',
        isPublished: false,
      });
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  }, [eikenMaterial]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const getGenerateImageStatus = useCallback(async () => {
    if (!eikenMaterial) return;

    try {
      let attempts = 0;

      // Create new AbortController for this polling session
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Start polling
      while (attempts < MAX_ATTEMPTS) {
        // Check if we need to abort
        if (signal.aborted) {
          console.log('Image status polling aborted');
          return;
        }

        attempts++;

        try {
          const statusResponse = await EikenMaterialResource.fetchGenerateImageStatus(
            eikenMaterial.id
          );

          // Check again if aborted after the async call
          if (signal.aborted) return;

          setGeneratingImageStatus({
            progress: statusResponse.progress,
            status: statusResponse.status,
          });

          console.log(`Polling attempt ${attempts}:`, statusResponse);

          if (['failed', 'not-found'].includes(statusResponse.status)) {
            break;
          }

          if (statusResponse.status === 'completed' && statusResponse.imageUrl) {
            setGeneratedImageUrl(statusResponse.imageUrl);
            // Note: move this to useEffect, so that it would upload the generated url instead of the placeholder
            // await doImageUpload();
            break;
          }

          // Wait for next poll interval
          await new Promise((resolve) => {
            const timeoutId = setTimeout(resolve, POLL_INTERVAL);

            // Attach abort handler to clear the timeout
            signal.addEventListener('abort', () => {
              clearTimeout(timeoutId);
              resolve(undefined);
            });
          });

          // Check if aborted after the timeout
          if (signal.aborted) return;
        } catch (error) {
          console.error('Error checking generation status:', error);
          // Consider whether to retry or break based on error type
          break;
        }
      }

      if (!generatedImageUrl) {
        console.warn('Image generation timed out or failed');
        // TODO: Handle timeout/failure case, show error message to user
      }
    } catch (error) {
      console.error('Error starting image generation:', error);
      // TODO: Show error message to user
    } finally {
      setGeneratingImageStatus(null);
    }
  }, [eikenMaterial, generatedImageUrl]);

  const handleGenerateImage = async () => {
    if (!eikenMaterial) return;

    await EikenMaterialResource.generateImage({
      id: eikenMaterial.id,
      prompt: imagePrompt,
      transitionArrowLabels: [transitionArrowLabel1, transitionArrowLabel2],
    });

    setGeneratingImageStatus({ status: 'active', progress: 0 });
    getGenerateImageStatus();
  };

  async function handleSubmit(data: FormData) {
    try {
      setIsLoading(true);

      if (eikenMaterial?.generatedImageStatus === 'generated') {
        await doImageUpload();
      }

      const formData: EikenMaterialGeneratedFormData = {
        referenceTextTitle: data.title,
        referenceText: data.pronunciationQuestion,
        imagePrompt: data.imagePrompt,
        imageText: data.imageText,
        content: {
          q1: data.q1,
          q2: data.q2,
          q3: data.q3,
          q4: data.q4,
          pronunciation: data.pronunciation,
        },
        transitionArrowLabels: [transitionArrowLabel1, transitionArrowLabel2],
      };

      await saveMaterial.submit(formData);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (
      eikenMaterial &&
      (eikenMaterial.generatedImageStatus === 'generating' || !eikenMaterial.generatedImage)
    ) {
      getGenerateImageStatus();
    }

    // Cleanup function to abort any ongoing polling when component unmounts
    return () => {
      if (abortControllerRef.current) {
        console.log('Aborting image status polling on unmount');
        abortControllerRef.current.abort();
      }
    };
  }, [eikenMaterial, getGenerateImageStatus, generatedImageUrl]);

  async function downloadImage() {
    setDownloading(true);
    const imagePreview = document.getElementById('image-preview');
    if (!imagePreview) return;

    try {
      const dataUrl = await toJpeg(imagePreview, imageConfig);
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = 'image.jpeg';

      document.body.appendChild(a);
      a.click();
    } catch (error) {
      console.error('Error downloading image:', error);
    } finally {
      setDownloading(false);
    }
  }

  async function handleDeleteTest() {
    if (eikenMaterial?.id) {
      await EikenMaterialResource.deleteOne(eikenMaterial.id);
    }
    navigate(`/eiken-materials`);
  }

  return (
    <Box sx={{ p: 2, background: '#fafbfc', minHeight: '100vh' }}>
      <Form onSubmit={handleSubmit} disabled={isLoading || saveMaterial.loading}>
        <Paper elevation={1} sx={{ p: 4, borderRadius: 3, maxWidth: 900, mx: 'auto' }}>
          {isEditTitle ? (
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="h4" fontWeight={600} mr={1}>
                {referenceTextTitle}
              </Typography>
              <IconButton size="small" onClick={() => setIsEditTitle(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <>
              <Typography fontWeight={700} mb={1}>
                Title
              </Typography>
              <TextField
                fullWidth
                multiline
                name="title"
                minRows={1}
                placeholder="Enter a title"
                variant="outlined"
                value={referenceTextTitle}
                onChange={(e) => setReferenceTextTitle(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': {
                    '& textarea': {
                      resize: 'vertical',
                    },
                  },
                }}
              />
            </>
          )}
          <Typography fontWeight={700} mb={1}>
            Pronunciation paragraph text
          </Typography>
          <TextField
            fullWidth
            multiline
            name="pronunciationQuestion"
            minRows={2}
            placeholder="Enter a description"
            variant="outlined"
            value={referenceText}
            onChange={(e) => setReferenceText(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiInputBase-root': {
                '& textarea': {
                  resize: 'vertical',
                },
              },
            }}
          />
          <Grid container spacing={3}>
            <div className="grid grid-cols-[auto_1fr] gap-4 w-full pt-5 pl-5">
              {/* Image upload section */}
              <div className="w-[350px]">
                <Typography fontWeight={700} mb={1}>
                  Image
                </Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fff',
                    position: 'relative',
                    p: 1,
                  }}
                >
                  <div id="image-preview" className="flex flex-col">
                    {(referenceTextTitle || referenceText) && (
                      <div className="border border-black rounded-md p-2 flex flex-col m-1">
                        <Typography
                          variant="caption"
                          color="text.primary"
                          align="center"
                          fontFamily={'Times New Roman'}
                          fontSize={10}
                          fontWeight={700}
                        >
                          {referenceTextTitle}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.primary"
                          fontFamily={'Times New Roman'}
                          fontSize={9}
                        >
                          {referenceText}
                        </Typography>
                      </div>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontFamily={'Times New Roman'}
                      paddingLeft={2}
                      paddingRight={2}
                      fontSize={11}
                    >
                      Your story should begin with this sentence:
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.primary"
                      fontFamily={'Times New Roman'}
                      paddingLeft={2}
                      paddingRight={2}
                      fontSize={11}
                      fontWeight={700}
                    >
                      {imageText}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 'auto',
                        width: '100%',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {generatingImageStatus && (
                        <Box
                          sx={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'absolute',
                            background: 'rgba(0, 0, 0, 0.25)',
                            inset: 0,
                            backdropFilter: 'blur(4px)',
                          }}
                        >
                          {generatingImageStatus?.status === 'active' && (
                            <LoadingState progress={generatingImageStatus.progress} />
                          )}
                          {generatingImageStatus?.status === 'failed' && (
                            <Typography sx={{ color: 'white' }}>
                              Failed to generate image
                            </Typography>
                          )}
                        </Box>
                      )}

                      <img
                        src={generatedImageUrl || placeholder}
                        alt="Generated"
                        style={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  </div>
                </Box>
                <Box display="flex" gap={1} mb={1}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleGenerateImage}
                    disabled={generatingImageStatus !== null}
                  >
                    {generatingImageStatus
                      ? 'Generating...'
                      : generatedImageUrl
                      ? 'Regenerate'
                      : 'Generate'}
                  </Button>
                  <Button
                    onClick={() => downloadImage()}
                    variant="outlined"
                    color="primary"
                    fullWidth
                    disabled={!generatedImageUrl || downloading}
                    className="flex gap-2"
                  >
                    {downloading && <CircularProgress size={16} />}
                    Download
                  </Button>
                </Box>
              </div>
              {/* Text fields section */}
              <div>
                <>
                  <Typography fontWeight={700} mb={1}>
                    Image text
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    name="imageText"
                    minRows={1}
                    maxRows={1}
                    placeholder={`Enter the starting sentence`}
                    variant="outlined"
                    value={imageText}
                    onChange={(e) => setImageText(e.target.value)}
                    sx={{
                      mb: 3,
                      '& .MuiInputBase-root': {
                        '& textarea': {
                          resize: 'vertical',
                        },
                      },
                    }}
                  />
                </>
                <>
                  <Typography fontWeight={700} mb={1}>
                    Image Prompt Text
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    name="imagePrompt"
                    minRows={2}
                    maxRows={4}
                    placeholder={`Enter Image promt text`}
                    variant="outlined"
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    sx={{
                      mb: 3,
                      '& .MuiInputBase-root': {
                        '& textarea': {
                          resize: 'vertical',
                        },
                      },
                    }}
                  />
                </>
                {/* Transition Arrow Labels Inputs */}
                <Typography fontWeight={700} mb={1} mt={2}>
                  Transition arrow label text
                </Typography>
                <Box display="flex" gap={2} mb={3}>
                  <Box flex={1}>
                    <Typography fontWeight={400} mb={1}>
                      Left/First arrow
                    </Typography>
                    <TextField
                      fullWidth
                      name="transitionArrowLabel1"
                      placeholder="Enter first transition label"
                      value={transitionArrowLabel1}
                      onChange={(e) => setTransitionArrowLabel1(e.target.value)}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography fontWeight={400} mb={1}>
                      Right/Second arrow
                    </Typography>
                    <TextField
                      fullWidth
                      name="transitionArrowLabel2"
                      placeholder="Enter second transition label"
                      value={transitionArrowLabel2}
                      onChange={(e) => setTransitionArrowLabel2(e.target.value)}
                    />
                  </Box>
                </Box>
                <>
                  <Typography fontWeight={700} mb={1}>
                    Pronunciation question text
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    name="pronunciation"
                    minRows={1}
                    maxRows={1}
                    placeholder={`Enter a pronunciation question`}
                    variant="outlined"
                    value={pronunciation}
                    onChange={(e) => setPronunciation(e.target.value)}
                    sx={{
                      mb: 3,
                      '& .MuiInputBase-root': {
                        '& textarea': {
                          resize: 'vertical',
                        },
                      },
                    }}
                  />
                </>

                <Typography fontWeight={700} mb={1}>
                  Question 1 text
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  name={`q1`}
                  minRows={1}
                  maxRows={1}
                  placeholder={`Enter a question 1`}
                  value={q1}
                  onChange={(e) => setQ1(e.target.value)}
                  variant="outlined"
                  sx={{
                    mb: 3,
                    '& .MuiInputBase-root': {
                      '& textarea': {
                        resize: 'vertical',
                      },
                    },
                  }}
                />
                <Typography fontWeight={700} mb={1}>
                  Question 2 text
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  name={`q2`}
                  minRows={1}
                  maxRows={1}
                  placeholder={`Enter a question 2`}
                  value={q2}
                  onChange={(e) => setQ2(e.target.value)}
                  variant="outlined"
                  sx={{
                    mb: 3,
                    '& .MuiInputBase-root': {
                      '& textarea': {
                        resize: 'vertical',
                      },
                    },
                  }}
                />
                <Typography fontWeight={700} mb={1}>
                  Question 3 text
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  name={`q3`}
                  minRows={1}
                  maxRows={1}
                  placeholder={`Enter a question 3`}
                  value={q3}
                  onChange={(e) => setQ3(e.target.value)}
                  variant="outlined"
                  sx={{
                    mb: 3,
                    '& .MuiInputBase-root': {
                      '& textarea': {
                        resize: 'vertical',
                      },
                    },
                  }}
                />
                <Typography fontWeight={700} mb={1}>
                  Question 4 text
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  name={`q4`}
                  minRows={1}
                  maxRows={1}
                  placeholder={`Enter a question 4`}
                  value={q4}
                  onChange={(e) => setQ4(e.target.value)}
                  variant="outlined"
                  sx={{
                    mb: 3,
                    '& .MuiInputBase-root': {
                      '& textarea': {
                        resize: 'vertical',
                      },
                    },
                  }}
                />
              </div>
            </div>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              variant="outlined"
              onClick={handleDeleteTest}
              color="error"
              disabled={!eikenMaterial?.id}
            >
              Delete test
            </Button>
            <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 180 }}>
              Save question
            </Button>
          </Box>
        </Paper>
      </Form>
    </Box>
  );
}

type LoadingStateProps = {
  progress: number;
};

const LoadingState = ({ progress }: LoadingStateProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      color: 'white',
    }}
  >
    <CircularProgress size={25} color="inherit" />
    <Typography variant="caption" fontSize={10} align="center">
      <b>Preparing the image for you... {progress}%</b>
      <br />
      (Estimated wait: ~{Math.ceil((100 - progress) / 20)} minutes)
    </Typography>
  </Box>
);
